import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Input from 'components/common/Input';
import { Button, Card, Collapse, Form, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useFieldArray, useForm, useFormContext } from 'react-hook-form';
import Flex from 'components/common/Flex';
import Background from 'components/common/Background';
import bg2 from 'assets/img/icons/spot-illustrations/corner-3.png';
import SimpleBarReact from 'simplebar-react';
import { useSearchParams } from 'react-router-dom';
import SoftBadge from 'components/common/SoftBadge';

const defaultValues = { item: '', quantity: '', references: [] };

const CompositionForm = ({ selectedItem, item, items, append, update, setOpen, setSelectedItems }) => {
 const [searchParams, setSearchParams] = useSearchParams();
 const {
  reset,
  watch,
  setValue,
  register,
  handleSubmit,
  formState: { errors }
 } = useForm({ defaultValues });

 const itm = watch('item');

 const [select, setSelect] = useState({});
 const [options, setOptions] = useState([]);

 useEffect(() => {
  setSelect(items.find(item => item.value === itm) ?? {});
 }, [itm]);

 useEffect(() => {
  const optionsGroups = item?.references.map(e => ({
   label: e.reference,
   options: e.properties.map(a => ({ value: a._id, label: a.name }))
  }));

  setOptions(optionsGroups ?? []);
 }, [item]);

 useEffect(() => {
  reset({ ...defaultValues, ...selectedItem });
 }, [selectedItem]);

 const onSubmitData = newD => {
  const { index, ...newData } = newD;

  if (index >= 0) update(index, newData);
  else append(newData);

  const regroupRef = references => {
   return options.reduce((filteredGroups, group) => {
    const options = group.options.filter(option => references.some(a => a === option.value)).map(e => e.label);
    if (options.length) filteredGroups.push({ ...group, options });
    return filteredGroups;
   }, []);
  };

  setSelectedItems(itm => itm.filter(e => e.code !== newD?.item).concat({ ...item, options: regroupRef(newData?.references) }));

  reset(defaultValues);

  return setOpen(false);
 };

 const onError = err => console.log('se genero un error en el formulario', err);

 return (
  <>
   <Row className="gx-1">
    <Input
     type="selectMulti"
     name="item"
     errors={errors}
     placeholder="Seleccione item..."
     options={items}
     formGroupProps={{ className: 'mb-1 mb-md-0 col-9 col-md-10' }}
     formControlProps={{
      isClearable: true,
      isMulti: false,
      closeMenuOnSelect: true,
      value: select,
      onChange: newValue => {
       searchParams.set('code', newValue.value);
       setSearchParams(searchParams);
       setValue('item', newValue.value);
      }
     }}
    />

    <Input
     type="text"
     name="quantity"
     placeholder="ctd"
     formGroupProps={{ className: 'col-3 col-md-2 ps-1 pe-0' }}
     formControlProps={{
      ...register('quantity', {
       required: 'requerido',
       validate: value => !isNaN(value) || 'Deve ser num'
      }),
      className: 'text-center fs-1 py-1 px-2',
      onFocus: e => e.target.select()
     }}
    />
   </Row>

   <SimpleBarReact className="m-0" forceVisible="x" autoHide={true} style={{ whiteSpace: 'nowrap', overflowX: 'auto' }}>
    <Flex direction="column" className="my-2">
     {options.map((ref, indx) => (
      <div key={indx} className="px-2 d-flex align-items-center border-bottom border-bottom-2">
       <h6 className="flex-fill fs--1 mb-0">{ref?.label}:</h6>

       {ref?.options.map((e, i) => (
        <Form.Check
         key={i}
         type="radio"
         id={e?.value}
         label={e?.label}
         value={e?.value}
         className="flex-fill form-label-nogutter ms-3"
         {...register(`references.${indx}`, { required: true })}
        />
       ))}
      </div>
     ))}
    </Flex>
   </SimpleBarReact>

   <Button
    variant="falcon-success"
    size="sm"
    type="button"
    className="float-end"
    onClick={() => handleSubmit(onSubmitData, onError)()}
   >
    Agregar
   </Button>
  </>
 );
};

const Composition = ({ item, items }) => {
 const { control } = useFormContext();
 const { fields, append, update, remove } = useFieldArray({ control, name: 'composition' });

 const [open, setOpen] = useState(false);
 const [selectedItem, setSelectedItem] = useState({});
 const [selectedItems, setSelectedItems] = useState([]);

 useEffect(() => {
  if (!open) setSelectedItem({});
 }, [open]);

 return (
  <Card className="mb-3">
   <Card.Body>
    <Flex className="g-2" direction="row" justifyContent="start" alignItems="center" wrap="nowrap">
     <div className="flex-grow-1 pe-2 border-end border-end-2">
      <OverlayTrigger
       overlay={
        <Tooltip style={{ position: 'fixed' }} id="finalPriceTooltip">
         Product final price
        </Tooltip>
       }
      >
       <span className="d-flex text-primary fs-0">
        <FontAwesomeIcon icon="question-circle" />
        <h5 className="ms-1 mb-0 fs-0">Precios & Referencias</h5>
       </span>
      </OverlayTrigger>

      <p className="m-0 fs--2 text-muted">Crea nuevas referencias de precio</p>
     </div>

     <Button variant="falcon-primary" type="button" className="icon-item icon-item-sm ms-3" onClick={() => setOpen(!open)}>
      <FontAwesomeIcon className="" icon={open ? 'chevron-up' : 'chevron-down'} />
     </Button>
    </Flex>

    {fields.length ? (
     <Flex alignItems="center" wrap="wrap" className="mb-2">
      {fields.map((ref, index) => {
       const { item, quantity } = ref;
       const refs = selectedItems.find(e => e.code === item);
       return (
        <Card key={index} className="bg-transparent border mt-3 w-100">
         <Background image={bg2} className="bg-card" />
         <Card.Body className="hover-actions-trigger fs--1 p-3 pb-2">
          <div className="d-flex justify-content-between align-items-start">
           <div className="d-flex align-items-center">
            <h6 className="mb-0 me-3 d-none d-md-block">Item:</h6>
            <h6 className="mb-0 fs-0 display-6 d-inline-block text-truncate" style={{ maxWidth: '50vw' }}>
             {refs?.name}
            </h6>
           </div>
           <div className="d-flex align-items-center">
            <h6 className="mb-0 me-3">Ctd:</h6>
            <h6 className="mb-0 fs-0 display-6">{quantity}</h6>
           </div>
          </div>

          <SimpleBarReact className="m-0" forceVisible="x" autoHide={true} style={{ whiteSpace: 'nowrap', overflowX: 'auto' }}>
           <Flex className="mt-1">
            {refs?.options.map((ref, i) => (
             <div key={i} className="px-2 flex-fill">
              <h6 className="text-700 fs--2 mb-1">
               {ref?.label}
               <SoftBadge pill bg="success" className="ms-2">
                {ref?.options[0]}
                <FontAwesomeIcon icon="caret-up" className="ms-1" />
               </SoftBadge>
              </h6>
             </div>
            ))}
           </Flex>
          </SimpleBarReact>

          <div className="end-0 top-0 p-1 p-md-0 hover-actions translate-middle-y">
           <Button
            variant="link"
            type="button"
            className="d-flex align-items-center fs--1 text-700 p-0"
            onClick={() => {
             setOpen(true);
             setSelectedItem({ ...ref, index });
            }}
           >
            <FontAwesomeIcon icon="pencil-alt" />
           </Button>

           <Button
            size="sm"
            variant="link"
            type="button"
            className="d-flex align-items-center fs-1 text-700 p-0 ms-3"
            onClick={() => remove(index)}
           >
            <FontAwesomeIcon icon="times-circle" />
           </Button>
          </div>
         </Card.Body>
        </Card>
       );
      })}
     </Flex>
    ) : (
     <></>
    )}

    <Collapse in={open}>
     <div className="mt-3">
      <CompositionForm
       item={item}
       items={items}
       append={append}
       update={update}
       setOpen={setOpen}
       selectedItem={selectedItem}
       setSelectedItems={setSelectedItems}
      />
     </div>
    </Collapse>
   </Card.Body>
  </Card>
 );
};

Composition.propTypes = {
 item: PropTypes.object,
 items: PropTypes.array
};

CompositionForm.propTypes = {
 selectedItem: PropTypes.object,
 setSelectedItems: PropTypes.func,
 items: PropTypes.array,
 item: PropTypes.object,
 append: PropTypes.func,
 update: PropTypes.func,
 setOpen: PropTypes.func
};

export default Composition;
